/** What each table row shows and offers, computed once per render from the props (pure):
 *  its name, whether its controls are live, what changed against the baseline, the locks
 *  with their reasons (a rule, a limit), its check-in order, its actions and its focus
 *  stops — so the rendering and the keyboard agree on what exists.
 *
 *  `keepUnchanged` hands back the previous model of a row whose content did not change, so
 *  the memoized rows re-render only where an edit shows (the row edited, the rows it
 *  renumbers or locks) — not all of them on every action. */
import {
  checkinOrder,
  effectiveCategoryRows,
  rowChanges,
  rowName,
  storedCategoryRows,
  type AssocField,
  type AssocFieldChange,
} from './associationsReducer';
import type { Flag } from './flags';
import type { ACTION_COLS } from './gridNav';
import { flagLock, limitLock } from './rules';
import type { AssocRow, InheritedRow } from './types';

export type ActionCol = (typeof ACTION_COLS)[number];

export interface RowModel {
  /** The row's id in the focus grid (`own:<key>` / `inherited:<key>`). */
  navId: string;
  kind: 'own' | 'inherited';
  row: AssocRow;
  /** The inherited row (with `overriddenBy`), for an inherited one. */
  inherited?: InheritedRow;
  name: string;
  /** An own row, in edit mode, not removed: its controls are live. */
  live: boolean;
  /** The stored row it is compared with (an own stored row with a baseline). */
  base?: AssocRow;
  changes: AssocFieldChange[];
  /** Changed fields; `counts` when min or max changed. */
  changed: ReadonlySet<AssocField | 'counts'>;
  /** Why a flag cannot be changed right now (live rows only). */
  locks: Partial<Record<Flag, string>>;
  /** Place in the check-in order (effective rows only). */
  order: number | null;
  canUp: boolean;
  canDown: boolean;
  /** The action buttons that can take focus, in slot order. */
  actions: ActionCol[];
  /** Focus stops, left to right. */
  stops: string[];
  /** The first inherited row (a heavier rule above it). */
  firstInherited: boolean;
}

export function buildRowModels({
  rows,
  inherited,
  baseline,
  editable,
  flags,
}: {
  rows: readonly AssocRow[];
  /** With `overriddenBy` resolved (`withOverrides`). */
  inherited: readonly InheritedRow[];
  baseline?: ReadonlyMap<string, AssocRow>;
  editable: boolean;
  /** The visible flag columns. */
  flags: readonly Flag[];
}): RowModel[] {
  const effective = effectiveCategoryRows(rows, inherited);
  // With a baseline, a limit lock follows the server's no-worse rule (a stored breach may
  // persist, not grow); without one, the plain limit.
  const stored = baseline ? storedCategoryRows(baseline, inherited) : undefined;
  const order = checkinOrder(rows, inherited);
  const liveKeys = rows.filter((r) => r.state !== 'removed').map((r) => r.key);
  const out: RowModel[] = [];

  for (const row of rows) {
    const live = editable && row.state !== 'removed';
    const base = row.state === 'stored' ? baseline?.get(row.key) : undefined;
    const changes = base ? rowChanges(base, row) : [];
    const changed = new Set<AssocField | 'counts'>(changes.map((c) => c.field));
    if (changed.has('min_tags') || changed.has('max_tags')) changed.add('counts');
    const locks: Partial<Record<Flag, string>> = {};
    if (live) {
      const flagRow = { flag: (f: string) => row.flags[f as Flag] === true };
      for (const f of flags) {
        const lock =
          flagLock(f, flagRow) ??
          (f === 'checkin' || f === 'boarding'
            ? limitLock(f, row.flags[f], effective, stored)
            : null);
        if (lock) locks[f] = lock;
      }
    }
    const li = liveKeys.indexOf(row.key);
    const canUp = live && li > 0;
    const canDown = live && li >= 0 && li < liveKeys.length - 1;
    const actions: ActionCol[] = [];
    if (live) {
      if (canUp) actions.push('act:up');
      if (canDown) actions.push('act:down');
      if (changes.length) actions.push('act:revert');
      actions.push('act:remove');
    } else if (editable && row.state === 'removed') {
      actions.push('act:restore');
    }
    out.push({
      navId: `own:${row.key}`,
      kind: 'own',
      row,
      name: rowName(row),
      live,
      ...(base ? { base } : {}),
      changes,
      changed,
      locks,
      order: order.get(row.key) ?? null,
      canUp,
      canDown,
      actions,
      stops: ['category', 'counts', ...flags, ...actions],
      firstInherited: false,
    });
  }

  inherited.forEach((row, i) => {
    const actions: ActionCol[] =
      editable && !row.overriddenBy && row.category ? ['act:override'] : [];
    out.push({
      navId: `inherited:${row.key}`,
      kind: 'inherited',
      row,
      inherited: row,
      name: rowName(row),
      live: false,
      changes: [],
      changed: new Set(),
      locks: {},
      order: order.get(row.key) ?? null,
      canUp: false,
      canDown: false,
      actions,
      stops: ['category', 'counts', ...flags, ...actions],
      firstInherited: i === 0 && rows.length > 0,
    });
  });
  return out;
}

// ---------------------------------------------------------------------------
// Identity across renders
// ---------------------------------------------------------------------------

type Same<T> = { [K in keyof T]-?: (a: T[K], b: T[K]) => boolean };

const is = <V>(a: V, b: V) => a === b;
const sameItems = (a: readonly unknown[], b: readonly unknown[]) =>
  a.length === b.length && a.every((x, i) => x === b[i]);
/** Same own values, one level deep. */
const sameFields = <V extends object>(a: V | undefined, b: V | undefined) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a) as (keyof V)[];
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
};

/** How two models of one row compare — every field listed (the type insists), so a field
 *  added to `RowModel` cannot be left out. */
const SAME: Same<RowModel> = {
  navId: is,
  kind: is,
  // Field by field: the reducer keeps an untouched row's object, but `withOverrides`
  // copies every inherited row on each change.
  row: sameFields,
  inherited: sameFields,
  name: is,
  live: is,
  base: is,
  // Derived from `row` and `base` alone.
  changes: () => true,
  changed: () => true,
  locks: sameFields,
  order: is,
  canUp: is,
  canDown: is,
  actions: sameItems,
  stops: sameItems,
  firstInherited: is,
};
const SAME_KEYS = Object.keys(SAME) as (keyof RowModel)[];

const sameModel = (a: RowModel, b: RowModel) =>
  SAME_KEYS.every((k) => (SAME[k] as (x: unknown, y: unknown) => boolean)(a[k], b[k]));

/** `next`, with each row whose model is unchanged since `prev` keeping its previous object. */
export function keepUnchanged(prev: readonly RowModel[], next: RowModel[]): RowModel[] {
  if (prev.length === 0) return next;
  const before = new Map(prev.map((m) => [m.navId, m]));
  return next.map((m) => {
    const p = before.get(m.navId);
    return p && sameModel(p, m) ? p : m;
  });
}
